import helpers from "./helpers";
import api from "./api";
import { hasher } from "./alpine/stores/hasher";

export default {
  getProductQuery() {
    return `
      id
      title
      handle
      tags
      productType
      createdAt
      totalInventory
      vendor
      options {
        name,
        values
      }
      featuredImage {
        altText
        url
      }
      isGiftCard
      images(first: 2) {
        edges {
          node {
            altText
            url
          }
        }
      }
      priceRange {
        maxVariantPrice {
          amount
        }
        minVariantPrice {
          amount
        }
      }
      variants(first: 100) {
        edges {
          node {
            id
            availableForSale
            selectedOptions {
              value
            }
            compareAtPrice {
              amount
            }
            image {
              altText
              url
            }
          }
        }
      }
      metafields(identifiers: [
        {namespace: "ap21", key: "matchingJacket"},
        {namespace: "ap21", key: "matchingTrouser"},
        {namespace: "ap21", key: "matchingVest"}
      ]) {
        namespace
        key
        value
        reference {
          ... on Product {
            id
            handle
            title
            priceRange {
              minVariantPrice {
                amount
              }
            }
            variants(first: 1) {
              edges {
                node {
                  price {
                    amount
                  }
                }
              }
            }
          }
        }
      }
    `;
  },
  getRelatedProductsQuery(tags) {
    const tagQuery = tags.map((tag) => `tag:'${tag}'`).join(" OR ");
    const currency = window.Alpine.store('currency');

    return `
    query getRelatedProducts @inContext(country: ${currency.getCurrentCurrency()?.country || window.Shopify.country}) {
      products(first: 10, query: "(${tagQuery})") {
        edges {
          node {
            id
            title
            handle
            images(first: 20) {
              edges {
                node {
                  altText
                  src
                }
              }
            }
          }
        }
      }
    }
  `;
  },
  // Helper function to generate the GraphQL query
  getRelatedProductsByHandleQuery(handles) {
    const handlesString = handles.map((handle) => `'${handle}'`).join(" OR ");
      const currency = window.Alpine.store('currency');
    return `
        query products @inContext(country: ${currency.getCurrentCurrency()?.country || window.Shopify.country}) {
            products(first: 20, query: "(${handlesString})") {
                edges {
                    node {
                        id
                        title
                        handle
                        tags

                        priceRange {
                            maxVariantPrice {
                              amount
                            }
                            minVariantPrice {
                              amount
                            }
                        }
                        variants(first: 100) {
                            edges {
                              node {
                                id
                                availableForSale
                                selectedOptions {
                                  value
                                }
                                compareAtPrice {
                                  amount
                                }
                                image {
                                  altText
                                  url
                                }
                              }
                            }
                          }
                        images(first: 10) {
                            edges {
                                node {
                                    src
                                    url
                                }
                            }
                        }
                    }
                }
            }
        }
    `;
  },
  async getInventoryDetails(ids) {
    await hasher.store().dispatchHash();
    
    const query = hasher.store().getQuery('get_inventory_details');
    if (!query) {
      console.error('get_inventory_details query not found in hasher store');
      return null;
    }

    const variables = {
      ids: ids.map(id => `gid://shopify/ProductVariant/${id}`)
    };

    const body = JSON.stringify({
      query,
      variables
    });

    const data = {
      options: {
        method: 'POST',
        headers: {
          'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
          'Content-Type': 'application/json',
          'X-MindArc-Hash': hasher.store().getHash('get_inventory_details'),
        },
        body: body,
        request_body: query,
      }
    }

    return await this.authenticatedFetch(data);
  },
  async authenticatedFetch(fetchDetails, version = "2024-01") {
    fetchDetails.url = `${helpers.getDomain()}/admin/api/${version}/graphql.json`;
    fetchDetails.shop_url = `${helpers.getDomain(false, true)}/admin/api/${version}/graphql.json`;
    fetchDetails.key = "SHOPIFY_API_AUTHENTICATED_KEY";
    // fetchDetails.hash = sig;

    const response = await fetch(`${helpers.getDomain()}/apps/arctheme/fetch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fetchDetails),
    });

    if (!response.ok) {
      console.error(`Error calling authenticated api: ${response.statusText}`);
    }
    return await response.json();
  },
  async authenticatedRestFetch(fetchDetails, endpoint) {
    fetchDetails.url = `${helpers.getDomain()}/admin/api/2024-07/${endpoint}.json`;
    fetchDetails.shop_url = `${helpers.getDomain(false,true)}/admin/api/2024-07/${endpoint}.json`;
    // fetchDetails.key = `${api.getAdminToken()}`;
    fetchDetails.key = `SHOPIFY_API_AUTHENTICATED_KEY`;
    // fetchDetails.hash = sig;

    const response = await fetch(`${helpers.getDomain()}/apps/arctheme/fetch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fetchDetails),
    });

    if (!response.ok) {
      console.error(`Error calling authenticated api: ${response.statusText}`);
    }

    console.log(response);
    return await response.json();
  },
  async unauthenticatedFetch(query, variables = null, storefront=true) {
    const url = `${helpers.getDomain()}/api/2024-07/graphql.json`;

    let headers = {
      "Content-Type": "application/graphql",
      "X-Shopify-Storefront-Access-Token": `${api.getStorefrontToken()}`
    };

    // Perform the GraphQL request
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: query,
    });

    return await response.json();
  },
  async getStockForProducts(productData, storefront = false) {
    const data = `{
            nodes(ids: [${productData.reduce((filtered, id) => {
              filtered.push('"' + "gid://shopify/Product/" + id + '"');
              return filtered;
            }, [])}]) {
              id
              ... on Product {
                ${this.getProductQuery()}
              }
            }
          }`;

    // Construct headers conditionally based on the 'storefront' flag
    let headers = {
      "Content-Type": "application/graphql",
    };

    headers["X-Shopify-Storefront-Access-Token"] =
      `${api.getStorefrontToken()}`; // This key to be stored elsewhere
    
    let transformedProducts = null;
    const response = await fetch(
      `${helpers.getDomain()}/api/2024-07/graphql.json`,
      {
        method: "POST",
        headers: headers,
        body: data,
      },
    );
    const jsonResponse = await response.json();
  },
  async getShopifyArticleList(tags) {
    const tagQuery = tags.map((tag) => `tag:'${tag}'`).join(" OR ");
    const data = `{
        articles(first: 10, query: "(${tagQuery})") {
            edges {
                node {
                    id
                    title
                    tags
                    excerpt
                    publishedAt
                    handle
                    blog {
                        handle
                    }
                    image {
                        src
                        altText
                    }
                }
            }
        }
    }`;

    // Construct headers conditionally based on the 'storefront' flag
    let headers = {
      "Content-Type": "application/graphql",
    };
    headers["X-Shopify-Storefront-Access-Token"] =
      `${api.getStorefrontToken()}`; // This key to be stored elsewhere

    let transformedArticles = null;
    const response = await fetch(
      `${helpers.getDomain()}/api/2024-07/graphql.json`,
      {
        method: "POST",
        headers: headers,
        body: data,
      },
    );

    const jsonResponse = await response.json();
    const articles = jsonResponse.data?.articles.edges.map((edge) => edge.node);

    // console.log(articles);

    transformedArticles = articles.map((article) =>
      this.transformGraphqlArticle(article),
    );

    return transformedArticles;
  },

  transformGraphqlArticle(article) {
    return {
      id: article.id,
      title: article.title,
      url: article.url,
      handle: article.handle,
      tags: article.tags,
      excerpt: article.excerpt,
      publishedAt: article.publishedAt,
      blog: article.blog
        ? {
            handle: article.blog.handle,
          }
        : null,
      image: article.image
        ? {
            src: article.image.src,
            altText: article.image.altText,
          }
        : null,
    };
  },
  async getShopifyProductList(productData) {
    const currency = window.Alpine.store('currency');
    const productIds = Array.isArray(productData)
      ? productData.filter((id) => id !== null && id !== undefined)
      : [];

    if (productIds.length === 0) {
      return [];
    }

    const maxIdsPerRequest = 250;
    let headers = {
      "Content-Type": "application/graphql",
    };
    headers["X-Shopify-Storefront-Access-Token"] =
      `${api.getStorefrontToken()}`; // This key to be stored elsewhere

    const transformedProducts = [];

    for (let start = 0; start < productIds.length; start += maxIdsPerRequest) {
      const productIdsChunk = productIds.slice(start, start + maxIdsPerRequest);
      const data = `query products @inContext(country: ${currency.getCurrentCurrency()?.country || window.Shopify.country}) {
            nodes(ids: [${productIdsChunk
              .map((id) => `"gid://shopify/Product/${id}"`)
              .join(',')}]) {
              id
              ... on Product {
                ${this.getProductQuery()}
              }
            }
          }`;

      const response = await fetch(
        `${helpers.getDomain()}/api/2024-07/graphql.json`,
        {
          method: "POST",
          headers: headers,
          body: data,
        },
      );

      if (!response.ok) {
        console.error(`getShopifyProductList: HTTP ${response.status} ${response.statusText}`);
        continue;
      }

      const jsonResponse = await response.json();

      if (jsonResponse.errors?.length) {
        console.error('getShopifyProductList: GraphQL errors', jsonResponse.errors);
        continue;
      }

      const products = jsonResponse.data?.nodes?.filter((node) => node) || [];
      transformedProducts.push(
        ...products.map((product) => this.transformGraphqlProduct(product)),
      );
    }

    return transformedProducts;
  },
  escapeTagForSearchQuery(tag) {
    if (typeof tag !== "string") {
      return "";
    }
    // Escape backslashes first, then single quotes, for safe embedding in single-quoted search strings.
    return tag.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  },
  async getRelatedProductsByTagList(tags) {
    const normalizedTags = Array.isArray(tags)
      ? tags.filter((t) => t != null && String(t).trim() !== "")
      : [];
    if (normalizedTags.length === 0) {
      return [];
    }
    const uniqueTags = [...new Set(normalizedTags)];
    const tagQuery = uniqueTags
      .map((tag) => `tag:'${this.escapeTagForSearchQuery(tag)}'`)
      .join(" OR ");
    const currency = window.Alpine.store('currency');
    const relatedProductsQuery = `
      query getRelatedProducts @inContext(country: ${currency.getCurrentCurrency()?.country || window.Shopify.country}) {
        products(first: 200, query: "(${tagQuery})") {
          edges {
            node {
              id
              handle
            }
          }
        }
      }
    `;
    let headers = {
      "Content-Type": "application/graphql",
      "X-Shopify-Storefront-Access-Token": `${api.getStorefrontToken()}`
    };

    const response = await fetch(
      `${helpers.getDomain()}/api/2024-07/graphql.json`,
      {
        method: "POST",
        headers: headers,
        body: relatedProductsQuery,
      },
    );

    if (!response.ok) {
      console.error(`getRelatedProductsByTagList: HTTP ${response.status} ${response.statusText}`);
      return [];
    }

    const jsonResponse = await response.json();

    if (jsonResponse.errors?.length) {
      console.error('getRelatedProductsByTagList: GraphQL errors', jsonResponse.errors);
      return [];
    }

    const relatedProducts = jsonResponse.data?.products?.edges.map(
      (edge) => edge.node,
    );

    return relatedProducts || [];
  },
  async getRelatedProducts(products, checkTag = "colour:") {
    const tags = products.flatMap((product) =>
      product.tags.filter((tag) => tag.startsWith(checkTag)),
    );
    const uniqueTags = [...new Set(tags)];
    const relatedProductsQuery = this.getRelatedProductsQuery(uniqueTags);

    let headers = {
      "Content-Type": "application/graphql",
      "X-Shopify-Storefront-Access-Token": `${api.getStorefrontToken()}`
    };

    const response = await fetch(
      `${helpers.getDomain()}/api/2024-07/graphql.json`,
      {
        method: "POST",
        headers: headers,
        body: relatedProductsQuery,
      },
    );

    const jsonResponse = await response.json();

    const relatedProducts = jsonResponse.data?.products?.edges.map(
      (edge) => edge.node,
    );

    return relatedProducts || [];
  },
  async getRelatedProductsByTag(product, tagPrefix = "colour:") {
    // Extract handles from tags that start with the given prefix
    const tags = product.tags
      .filter((tag) => tag.startsWith(tagPrefix))
      .map((tag) => tag.replace(tagPrefix, "").trim());

    const uniqueTags = [...new Set(tags)]; // Get unique handles

    if (uniqueTags.length > 0) {
      const relatedProductsQuery =
        this.getRelatedProductsByHandleQuery(uniqueTags);

      let headers = {
        "Content-Type": "application/graphql",
        "X-Shopify-Storefront-Access-Token": `${api.getStorefrontToken()}`
      };

      const response = await fetch(
        `${helpers.getDomain()}/api/2024-07/graphql.json`,
        {
          method: "POST",
          headers: headers,
          body: relatedProductsQuery,
        },
      );

      const jsonResponse = await response.json();

      const relatedProducts = jsonResponse.data?.products?.edges.map(
        (edge) => edge.node,
      );
      const transformedRelatedProducts = relatedProducts?.map((product) =>
        this.transformGraphqlProduct(product),
      );
      return transformedRelatedProducts || [];
    }
    return [];
  },
  async getRecommendedProducts(productId) {
    try {
      let headers = {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": `${api.getStorefrontToken()}`,
      };

      const response = await fetch(
        `${helpers.getDomain()}/recommendations/products.json?product_id=${productId}`,
        {
          method: "GET",
          headers: headers,
        },
      );

      if (!response.ok) {
        throw new Error(
          `Error fetching recommendations: ${response.statusText}`,
        );
      }

      const jsonResponse = await response.json();

      return jsonResponse.products || [];
    } catch (error) {
      console.error("Failed to fetch product recommendations:", error);
    }
  },
  transformPrice(price) {
    return price / 100;
  },
  transformGraphqlProduct(productNode, collectionHandle = null) {
    const hasOnlyDefaultVariant =
      (productNode?.options?.[0]?.values?.[0] ?? "") === "Default Title";

    return productNode
      ? {
          objectType: "product",
          productType: productNode.productType,
          isGiftCard: productNode.isGiftCard,
          handle: productNode.handle,
          url: collectionHandle
            ? `/collections/${collectionHandle}/products/${productNode.handle}`
            : `/products/${productNode.handle}`,
          id: parseInt(productNode.id.replace("gid://shopify/Product/", "")),
          tags: productNode.tags.map((tag) => tag),
          title: productNode.title,
          createdAt: productNode.createdAt,
          totalInventory: productNode.totalInventory,
          vendor: productNode.vendor,
          // metafield values are transformed into arrays for consistency
          metafields: productNode.metafields
            ? {
                // Transform ap21 namespace metafields with product references
                ap21: productNode.metafields
                  .filter((metafield) => metafield && metafield.namespace === 'ap21')
                  .reduce((acc, metafield) => {
                    acc[metafield.key] = {
                      value: metafield.reference || metafield.value
                    };
                    return acc;
                  }, {}),
                // Keep other metafields in original format
                ...productNode.metafields
                  .filter((metafield) => metafield && metafield.namespace !== 'ap21')
                  .reduce((acc, metafield) => {
                    if (metafield.value && typeof metafield.value === 'string' && metafield.value.includes("[")) {
                      acc[metafield.key] = JSON.parse(metafield.value).flat();
                    } else {
                      acc[metafield.key] = [metafield.value];
                    }
                    return acc;
                  }, {})
              }
            : {},
          variants: productNode.variants?.edges.map(({ node }) => {
            return {
              id: parseInt(
                node.id.replace("gid://shopify/ProductVariant/", ""),
              ),
              available: node.availableForSale,
              compare_at_price: node.compareAtPrice?.amount * 100 || null,
              options: node.selectedOptions.map((option) => option.value),
              image: {
                alt: node?.image?.altText
                  ? node.image.altText
                  : productNode.title,
                media_type: "image",
                src: node?.image?.url ? node.image.url : null,
              },
            };
          }),
          price_calculated: productNode.priceRange?.minVariantPrice.amount,
          compare_at_price_calculated:
            productNode.variants?.edges[0].node.compareAtPrice?.amount || null,
          price: productNode.priceRange?.minVariantPrice.amount * 100,
          compare_at_price:
            productNode.variants?.edges[0].node.compareAtPrice?.amount * 100 ||
            null,
          featuredImage: {
            alt: productNode?.featuredImage?.altText
              ? productNode.featuredImage.altText
              : productNode.title,
            media_type: "image",
            src: productNode?.featuredImage?.url
              ? productNode.featuredImage.url
              : null,
          },
          media: productNode.images?.edges.map((image) => {
            return {
              alt: image?.node?.altText
                ? image.node.altText
                : productNode.title,
              media_type: "image",
              src: image?.node?.url ? image.node.url : null,
            };
          }),
          has_only_default_variant: hasOnlyDefaultVariant,
          options: productNode.options?.map((option) => option.name),
          options_with_values: productNode.options?.map((option) => {
            return {
              name: option.name,
              position: productNode.options.indexOf(option) + 1,
              values: option.values,
            };
          }),
        }
      : null;
  },

  getShopifyImageUrl(
    originalUrl,
    width = null,
    height = null,
    customQueries = null,
  ) {
    const params = {
      width: width ? Math.ceil(width) : null,
      height: height ? Math.ceil(height) : null,
      ...customQueries,
    };

    const url = helpers.updateUrlParameters(originalUrl, params);
    return url;
  },
};
